import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { useForm, useFormState } from 'react-final-form';
import dynamic from 'next/dynamic';
import Icon from '../../Icon';
import FilterSmallIcon from '../../../public/static/images/new_search/icons/icon-filter-small.svg';
import { Button } from '../../ui/Button';
import { FiltersFloatButton, buttonStyles, SearchBarContainer } from './styled';
import { useUndoFilters } from '../../../hooks/useUndoFilters';
import useScreenSize from '../../../hooks/useScreenSize';
import { useStickyHeader } from '../../../hooks/useStickyHeader';
import {
  InitialFormValuesType,
  ModalParams,
  ModalTypeEnum,
  NewSearchBarProps
} from './types';
import { SearchPageFilters } from './SearchPageFilters';
import {
  BUILDING_TYPE_ID,
  SEARCH_RESULT_ROUTES
} from '../../../utils/helpers/constants';
import { useBGBannerPadding } from '../../../hooks/useBGBannerPadding';

const SearchBarModals = dynamic(
  () => import('./SearchBarModals').then(mod => mod.SearchBarModals),
  {
    ssr: false
  }
);

export const NewSearchBar = ({
  formEl,
  extraPadding,
  searchVariables
}: NewSearchBarProps) => {
  const { t } = useTranslation();
  const { values } = useFormState({
    subscription: { values: true }
  });
  const { change } = useForm();
  const undoFilters = useUndoFilters();
  const { isSmall, isSmallOrLargeDesktop } = useScreenSize();
  const [modalParams, setModalParams] = useState<ModalParams>(null);
  const [visitedHomePage, setVisitedHomePage] = useState(false);
  const router = useRouter();
  const isHomePage = router.pathname === '/';
  const isSalePage = router.asPath === SEARCH_RESULT_ROUTES.SALE;
  const isRentPage =
    router.asPath === SEARCH_RESULT_ROUTES.RENT ||
    router.asPath === SEARCH_RESULT_ROUTES.RENT_DAILY;
  const isItemsAllPage = router.pathname === SEARCH_RESULT_ROUTES.ITEMS_ALL;

  const [initialValues, setInitialValues] =
    useState<InitialFormValuesType | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);

  const { isFixed, headerHeight, showFloatButton } = useStickyHeader({
    containerRef,
    isHomePage,
    headerId: 'popup_header'
  });

  const isHomeOrItemsAllPage = isHomePage || isItemsAllPage;
  const isShowHomeFilters = isSalePage || isRentPage || isHomeOrItemsAllPage;
  const isSearchBarFixed = isFixed && !isHomeOrItemsAllPage;

  useBGBannerPadding(headerHeight, isHomePage);

  useEffect(() => {
    if (!values.category_id) {
      change('category_id', BUILDING_TYPE_ID.FLAT_ALL);
    }
  }, []);

  useEffect(() => {
    if (isHomePage) {
      sessionStorage.setItem('visitedHomePage', 'true');
      setVisitedHomePage(true);
    } else {
      const visited = sessionStorage.getItem('visitedHomePage') === 'true';
      setVisitedHomePage(visited);
    }
  }, [isHomePage]);

  const canGoBack =
    visitedHomePage &&
    !isHomePage &&
    isSmall &&
    typeof window !== 'undefined' &&
    window.history.length > 1;

  const handleGoBack = useCallback(() => {
    router.back();
  }, [router]);

  const openFilters = useCallback(() => {
    setInitialValues(values);
    setModalParams({ initialScreen: ModalTypeEnum.Filters });
  }, [values]);

  const hideFilters = useCallback(() => {
    setModalParams(null);
  }, []);

  const handleHideWithUndo = useCallback(() => {
    undoFilters(initialValues);
    setModalParams(null);
  }, [undoFilters, initialValues]);

  return (
    <>
      {isSearchBarFixed && <div style={{ height: headerHeight }} />}

      <SearchBarContainer
        ref={containerRef}
        $isSearchPage={!isShowHomeFilters}
        extraPadding={extraPadding}
        $isHomePage={isHomePage}
        data-cy="search-bar-container"
        isFixed={isSearchBarFixed}
        canGoBack={canGoBack}
      >
        <SearchPageFilters
          setModalParams={setModalParams}
          shortMode={(isSmall && isFixed) || (isSmall && isShowHomeFilters)}
          canGoBack={canGoBack && isFixed}
          onGoBack={handleGoBack}
          isShowHomeFilters={isShowHomeFilters}
        />

        {isSmallOrLargeDesktop && (
          <Button
            type="submit"
            cypress="new-search-find-button"
            stat="new-search-find-button"
            styles={buttonStyles}
            text={t('search.buttons.find')}
            onClick={() => {}}
          />
        )}

        {isSmall && isHomeOrItemsAllPage && (
          <FiltersFloatButton
            tabIndex={0}
            role="button"
            onClick={openFilters}
            onKeyPress={openFilters}
            id="search_float_button"
            data-stat="new-search-filters-float-button"
            style={{
              opacity: showFloatButton ? '1' : '0'
            }}
          >
            <Icon width={18} height={18} IconComponent={FilterSmallIcon} />
            {t('search.buttons.filters')}
          </FiltersFloatButton>
        )}
        <SearchBarModals
          formEl={formEl}
          hideFilters={hideFilters}
          modalParams={modalParams}
          handleHideWithUndo={handleHideWithUndo}
          searchVariables={searchVariables}
        />
      </SearchBarContainer>
    </>
  );
};
